import React from "react";
import Lottie from "react-lottie";

import cx from "classnames";
import { noop } from "lodash";

import { ReactComponent as StarOutlineDark } from "./css/Star-Outline-dark.svg";
import { ReactComponent as StarOutline } from "./css/Star-Outline.svg";
import { ReactComponent as StarSolidDark } from "./css/Star-Solid-dark.svg";
import { ReactComponent as StarSolid } from "./css/Star-Solid.svg";
import AnimationData from "./css/StarAnimation.json";

import styles from "./css/Star.module.css";

export { StarSolid, StarOutline };

const starAnimation = {
	loop: false,
	autoplay: true,
	animationData: AnimationData,
};

interface Props {
	selected: boolean;
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLDivElement>) => Promise<void>;
}

export default function StarButton({ selected, className, onClick }: Props) {
	const [newlySelected, setNewlySelected] = React.useState(false);

	const handleClick = React.useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			if (!onClick) return;
			const clickPromise = onClick(e);

			if (!clickPromise) {
				// Are you using a semantic-ui Popup? Do note that they replace the
				// onClick handler that you pass as the `trigger` prop, and their wrapper
				// does not return the value that your function returned. :D
				// Try wrapping the trigger in another element so Popup will add its
				// click handler to that instead of the StarButton.
				throw new Error();
			}

			clickPromise.then(() => setNewlySelected(true), noop);
		},
		[onClick, setNewlySelected],
	);

	const html = document.documentElement;
	const isDarkMode = html.dataset.theme == "theme-dark";

	return (
		<div
			className={cx(styles.container, className, { [styles.nohover]: newlySelected })}
			onClick={handleClick}
			data-test-id="stream-star"
		>
			{selected && newlySelected ? (
				<Lottie
					isClickToPauseDisabled
					isStopped={false}
					options={starAnimation}
					eventListeners={[{ eventName: "complete", callback: () => setNewlySelected(false) }]}
					width={26}
					height={26}
				/>
			) : selected && isDarkMode ? (
				<StarSolidDark />
			) : selected ? (
				<StarSolid />
			) : isDarkMode ? (
				<StarOutlineDark />
			) : (
				<StarOutline />
			)}
		</div>
	);
}
